import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ChipMode, ChipRailOutput } from "@/api/create-types";
import { Alert, Button } from "@/components/ui";
import type { RendererProps } from "./types";

type Sel = "skip" | ChipMode;

const MODE_PILLS: { value: Sel; label: string }[] = [
  { value: "skip", label: "Skip" },
  { value: "deep", label: "Deep" },
  { value: "brief", label: "Brief" },
  { value: "test", label: "Test" },
];

function ModePills({ value, onChange }: { value: Sel; onChange: (v: Sel) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {MODE_PILLS.map((m) => (
        <button
          key={m.value}
          type="button"
          onClick={() => onChange(m.value)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
            value === m.value
              ? m.value === "skip"
                ? "bg-ink-faint/20 text-ink-soft"
                : "bg-brand-600 text-white"
              : "bg-brand-50 text-brand-700 hover:bg-brand-100"
          }`}
        >
          {t(m.label)}
        </button>
      ))}
    </div>
  );
}

/** Renderer for the chip-rail planner (lecture-exercise-plan / math-tutor-plan,
 *  and optimistically learn-plan / deep-plan). Mirrors iOS LectureExerciseRecapView:
 *  pick per-topic and per-concept recap depth. */
export function ChipRailReview({ step, submitting, error, onAdvance }: RendererProps) {
  const { t } = useTranslation();
  const output = (step.output ?? { summary_blocks: [], topics: [], goals: [] }) as unknown as ChipRailOutput;
  const phrases = (output.summary_blocks ?? []).filter((b) => b.type === "phrase" && b.id);
  const topics = output.topics ?? [];
  const goals = output.goals ?? [];

  const [topicModes, setTopicModes] = useState<Record<string, Sel>>(() =>
    Object.fromEntries(topics.map((t) => [t.id, t.importance === "core" ? "deep" : "brief"])),
  );
  const [phraseModes, setPhraseModes] = useState<Record<string, Sel>>(() =>
    Object.fromEntries(phrases.map((p) => [p.id!, "skip"])),
  );
  const [focus, setFocus] = useState("");

  const selectedTopics = topics.filter((t) => topicModes[t.id] && topicModes[t.id] !== "skip").length;

  const submit = () => {
    const payload = {
      phrase_selections: phrases
        .filter((p) => phraseModes[p.id!] !== "skip")
        .map((p) => ({ phrase_id: p.id!, mode: phraseModes[p.id!] as ChipMode })),
      topic_selections: topics
        .filter((t) => topicModes[t.id] !== "skip")
        .map((t) => ({ topic_id: t.id, mode: topicModes[t.id] as ChipMode })),
      additional_focus: focus.trim()
        ? [{ label: focus.trim(), mode: "deep" as ChipMode }]
        : [],
    };
    onAdvance(payload);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold tracking-tight">{t("Plan your recap")}</h2>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Choose how deeply to revisit each topic.")} <b>{t("Deep")}</b> {t("teaches from the ground up,")}
          <b> {t("Brief")}</b> {t("refreshes,")} <b>{t("Test")}</b> {t("just checks you remember.")}
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      {goals.length > 0 && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-ink-soft">{t("This unit's goals")}</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-faint">
            {goals.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-ink-soft">{t("Topics")}</h3>
        {topics.map((tp) => (
          <div key={tp.id} className="card p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="font-semibold text-ink">{tp.label}</span>
                {tp.importance === "core" && (
                  <span className="ml-2 rounded-full bg-accent-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-accent-600">
                    {t("core")}
                  </span>
                )}
                {tp.summary && <p className="mt-0.5 text-sm text-ink-faint">{tp.summary}</p>}
              </div>
            </div>
            <div className="mt-3">
              <ModePills
                value={topicModes[tp.id] ?? "skip"}
                onChange={(v) => setTopicModes((s) => ({ ...s, [tp.id]: v }))}
              />
            </div>
          </div>
        ))}
      </div>

      {phrases.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-ink-soft">{t("Specific concepts (optional)")}</h3>
          <div className="card divide-y divide-black/5">
            {phrases.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-3">
                <span className="text-sm text-ink">{p.text}</span>
                <ModePills
                  value={phraseModes[p.id!] ?? "skip"}
                  onChange={(v) => setPhraseModes((s) => ({ ...s, [p.id!]: v }))}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block">
          <span className="mb-1.5 block text-sm font-semibold text-ink-soft">
            {t("Anything else to focus on? (optional)")}
          </span>
          <input
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            placeholder={t("A topic Eddi didn't list…")}
            className="w-full rounded-xl border border-black/10 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </label>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Button onClick={submit} size="lg" loading={submitting} disabled={selectedTopics === 0 && !focus.trim()}>
          {t("Generate recap")}
        </Button>
        {selectedTopics === 0 && !focus.trim() && (
          <span className="text-sm text-ink-faint">{t("Pick at least one topic.")}</span>
        )}
      </div>
    </div>
  );
}
